import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Card, Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select } from "../components/ui/Fields";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError, uploadAdminImage } from "../api/client";
import { FrameEditor, type Sel } from "../components/frames/FrameEditor";
import { FramePreview, SAMPLE_DP } from "../components/frames/FramePreview";
import { detectOpenings, candidatesToOpenings, type Candidate, type LabelMeta, type OpeningMeta, type SlotShape } from "../lib/frameDetect";

interface Saved { frameKey: string; imageUrl: string | null; canvasW: number; canvasH: number; openings: OpeningMeta[]; labels: LabelMeta[]; source: string; updatedAt?: string }
const PREVIEW_WIDTHS = [160, 240, 360, 480];
const KEY_RE = /^[a-z0-9_:\-.]{1,80}$/;

/**
 * Round 58 - Frame Editor. Pick a frame image (file, URL, or the bundled CP frame), the page reads its size and transparency,
 * suggests where the avatar openings are, and lets you drag/resize them. Preview shows a sample DP BEHIND the frame at several
 * display sizes. Save stores the numbers (in the image's own pixels) under the frame key; the app reads them and scales them.
 */
export function FrameEditorPage() {
  const { showToast } = useToast();
  const [key, setKey] = useState("cp_frame_pf_info");
  const [frameUrl, setFrameUrl] = useState<string>("/frames/ic_cp_frame_pf_info.png");
  const [publicUrl, setPublicUrl] = useState<string>("");     // what is stored as image_url (hosted URL), optional
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [hasAlpha, setHasAlpha] = useState<boolean | null>(null);
  const [openings, setOpenings] = useState<OpeningMeta[]>([]);
  const [labels, setLabels] = useState<LabelMeta[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selected, setSelected] = useState<Sel>(null);
  const [dpUrls, setDpUrls] = useState<string[]>([]);
  const [source, setSource] = useState<"auto" | "admin">("auto");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const savedForKey = useRef("");

  /** Reads size + alpha, runs detection. Returns the pixels' size. */
  const analyse = useCallback(async (url: string, cross: boolean) => {
    const img = new Image();
    if (cross) img.crossOrigin = "anonymous";
    await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error("could not load the image (for a URL on another site, download it and use Choose file)")); img.src = url; });
    const w = img.naturalWidth, h = img.naturalHeight;
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("canvas unavailable");
    ctx.drawImage(img, 0, 0);
    let data: Uint8ClampedArray;
    try { data = ctx.getImageData(0, 0, w, h).data; } catch { throw new Error("the browser blocked reading this image's pixels (cross-site URL) - download it and use Choose file"); }
    let transparent = 0;
    for (let i = 3; i < data.length; i += 4) if (data[i] < 32) transparent++;
    return { w, h, transparent, result: detectOpenings(data, w, h) };
  }, []);

  const loadImage = useCallback(async (url: string, cross: boolean, keepMeta: boolean) => {
    setBusy(true); setNote(null);
    try {
      const a = await analyse(url, cross);
      setFrameUrl(url); setSize({ w: a.w, h: a.h }); setHasAlpha(a.transparent > 0); setCandidates(a.result.candidates);
      if (a.transparent === 0) setNote("This image has no transparent pixels, so there is nothing to detect - add the openings by hand.");
      if (!keepMeta) {
        const o = candidatesToOpenings(a.result.candidates);
        setOpenings(o); setLabels([]); setSource("auto"); setSelected(null);
        if (o.length === 0 && a.transparent > 0) setNote("No clear avatar opening found. Candidates are listed below (even the unlikely ones) - add one, or draw it by hand.");
      }
    } catch (e) { setNote(e instanceof Error ? e.message : "could not read the image"); }
    finally { setBusy(false); }
  }, [analyse]);

  /** Loads what is saved for this key; when nothing is saved, falls back to auto-detection of the current image. */
  const loadSaved = useCallback(async (k: string) => {
    if (!KEY_RE.test(k)) { showToast("Key may use a-z 0-9 _ : - . (max 80)", "error"); return; }
    setBusy(true);
    try {
      const r = await apiRequest<Saved | null>("/admin/frame-meta", { params: { key: k } });
      savedForKey.current = k;
      setSaved(r);
      if (r) {
        setOpenings(r.openings); setLabels(r.labels ?? []); setSource(r.source === "auto" ? "auto" : "admin"); setPublicUrl(r.imageUrl ?? "");
        if (r.imageUrl) await loadImage(r.imageUrl, true, true);
        else await loadImage(frameUrl, false, true);
        setNote(r.imageUrl ? null : "Saved numbers loaded. The image shown is the one currently selected - make sure it is this frame's image.");
      } else {
        await loadImage(frameUrl, false, false);
        setNote("Nothing saved for this key yet - these are auto-detected suggestions. Check them, adjust, then Save.");
      }
    } catch (e) { showToast(e instanceof ApiError ? e.message : "could not load", "error"); }
    finally { setBusy(false); }
  }, [frameUrl, loadImage, showToast]);

  useEffect(() => { void loadSaved("cp_frame_pf_info"); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function onFile(f: File | null) {
    if (!f) return;
    const url = URL.createObjectURL(f);
    await loadImage(url, false, false);
  }
  async function uploadImage(f: File | null) {
    if (!f) return;
    setBusy(true);
    try { const r = await uploadAdminImage(f, "avatar_frame"); setPublicUrl(r.url); showToast("Image uploaded - its URL will be saved with the numbers", "success"); }
    catch (e) { showToast(e instanceof ApiError ? e.message : "upload failed", "error"); }
    finally { setBusy(false); }
  }
  function onDp(i: number, f: File | null) {
    if (!f) return;
    const next = [...dpUrls]; next[i] = URL.createObjectURL(f); setDpUrls(next);
  }

  const patch = (kind: "opening" | "label", id: string, p: Record<string, number | string>) => {
    if (kind === "opening") setOpenings((os) => os.map((o) => (o.id === id ? { ...o, ...p } as OpeningMeta : o)));
    else setLabels((ls) => ls.map((l) => (l.id === id ? { ...l, ...p } as LabelMeta : l)));
    setSource("admin");
  };
  const addOpening = (c?: Candidate) => {
    if (!size) return;
    let i = openings.length + 1; while (openings.some((o) => o.id === `o${i}`)) i++;
    const d = Math.round(Math.min(size.w, size.h) / 3);
    const o: OpeningMeta = c ? { id: `o${i}`, cx: c.cx, cy: c.cy, w: c.w, h: c.h, shape: c.shape } : { id: `o${i}`, cx: size.w / 2, cy: size.h / 2, w: d, h: d, shape: "circle" };
    setOpenings((os) => [...os, o]); setSelected({ kind: "opening", id: o.id }); setSource("admin");
  };
  const addLabel = () => {
    if (!size) return;
    let i = labels.length + 1; while (labels.some((l) => l.id === `text${i}`)) i++;
    const l: LabelMeta = { id: `text${i}`, cx: size.w / 2, cy: size.h * 0.8, w: size.w / 5, h: Math.max(16, size.h / 12) };
    setLabels((ls) => [...ls, l]); setSelected({ kind: "label", id: l.id }); setSource("admin");
  };
  const remove = () => {
    if (!selected) return;
    if (selected.kind === "opening") setOpenings((os) => os.filter((o) => o.id !== selected.id)); else setLabels((ls) => ls.filter((l) => l.id !== selected.id));
    setSelected(null); setSource("admin");
  };
  const rename = (kind: "opening" | "label", oldId: string, newId: string) => {
    const clean = newId.replace(/[^A-Za-z0-9_]/g, "").slice(0, 24);
    if (!clean) return;
    const taken = kind === "opening" ? openings.some((o) => o.id === clean && o.id !== oldId) : labels.some((l) => l.id === clean && l.id !== oldId);
    if (taken) return;
    if (kind === "opening") setOpenings((os) => os.map((o) => (o.id === oldId ? { ...o, id: clean } : o))); else setLabels((ls) => ls.map((l) => (l.id === oldId ? { ...l, id: clean } : l)));
    setSelected({ kind, id: clean });
  };

  const problems = useMemo(() => {
    const p: string[] = [];
    if (!size) return ["Load an image first."];
    if (!KEY_RE.test(key)) p.push("Frame key is invalid.");
    if (openings.length === 0) p.push("Add at least one avatar opening.");
    for (const o of openings) {
      if (o.cx - o.w / 2 < -0.01 || o.cy - o.h / 2 < -0.01 || o.cx + o.w / 2 > size.w + 0.01 || o.cy + o.h / 2 > size.h + 0.01) p.push(`Opening "${o.id}" sticks out of the image.`);
      if ((o.shape === "circle" || o.shape === "square") && Math.abs(o.w - o.h) > 0.5) p.push(`Opening "${o.id}" is a ${o.shape}, so width must equal height.`);
    }
    return p;
  }, [size, key, openings]);

  async function save() {
    if (!size || problems.length) return;
    setBusy(true);
    try {
      const r = await apiRequest<Saved>("/admin/frame-meta", { method: "POST", body: { key, imageUrl: publicUrl || null, canvasW: size.w, canvasH: size.h, openings, labels, source } });
      setSaved(r); showToast("Saved - the app will pick it up (cached copies refresh within ~10 minutes)", "success");
    } catch (e) { showToast(e instanceof ApiError ? e.message : "could not save", "error"); }
    finally { setBusy(false); }
  }

  const sel = selected?.kind === "opening" ? openings.find((o) => o.id === selected.id) : selected?.kind === "label" ? labels.find((l) => l.id === selected.id) : undefined;
  const stageW = size ? Math.min(900, size.w) : 600;

  return (
    <AppShell title="Frame Editor">
      <div className="space-y-4">
        <Card className="space-y-3 p-4">
          <div className="grid gap-3 md:grid-cols-3">
            <Input id="fk" label="Frame key" value={key} onChange={(e) => setKey(e.target.value.trim())} hint="cp_frame_pf_info, mall_item:<id>, custom:<name> ..." />
            <div>
              <label className="mb-1.5 block text-xs font-medium text-ink-muted">Frame image (PNG with transparency)</label>
              <input type="file" accept="image/png,image/webp" onChange={(e) => void onFile(e.target.files?.[0] ?? null)} className="text-xs text-ink-muted" />
              <button type="button" className="mt-1 block text-xs text-gold underline" onClick={() => void loadImage("/frames/ic_cp_frame_pf_info.png", false, false)}>use the bundled CP frame</button>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-ink-muted">Hosted image URL saved with the numbers (optional)</label>
              <Input id="pu" value={publicUrl} onChange={(e) => setPublicUrl(e.target.value)} placeholder="https://..." />
              <input type="file" accept="image/png,image/webp" onChange={(e) => void uploadImage(e.target.files?.[0] ?? null)} className="mt-1 text-xs text-ink-muted" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => void loadSaved(key)} disabled={busy}>Load saved for this key</Button>
            <Button size="sm" variant="secondary" onClick={() => void loadImage(frameUrl, frameUrl.startsWith("http"), false)} disabled={busy || !size}>Re-run auto-detect</Button>
            {size && <Badge tone="neutral">{size.w} x {size.h}px</Badge>}
            {hasAlpha !== null && <Badge tone={hasAlpha ? "success" : "warn"}>{hasAlpha ? "has transparency" : "no transparency"}</Badge>}
            <Badge tone={source === "auto" ? "gold" : "success"}>{source === "auto" ? "auto-detected, not reviewed" : "edited by admin"}</Badge>
            {saved && <span className="text-xs text-ink-faint">saved copy exists{saved.updatedAt ? ` (${new Date(saved.updatedAt).toLocaleString()})` : ""}</span>}
          </div>
          {note && <p className="text-xs text-warn">{note}</p>}
        </Card>

        {size && (
          <div className="grid gap-4 xl:grid-cols-[auto_minmax(280px,1fr)]">
            <Card className="overflow-auto p-4">
              <p className="mb-2 text-xs text-ink-muted">Drag a box to move it, drag a handle to resize. Green = avatar openings (DP goes behind the frame), amber = text boxes on top of the frame.</p>
              <FrameEditor frameUrl={frameUrl} canvasW={size.w} canvasH={size.h} openings={openings} labels={labels} dpUrls={dpUrls} stageWidth={stageW}
                selected={selected} onSelect={setSelected} onChange={(o, l) => { setOpenings(o); setLabels(l); setSource("admin"); }} />
            </Card>

            <Card className="space-y-3 p-4">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => addOpening()}>+ Opening</Button>
                <Button size="sm" variant="secondary" onClick={addLabel}>+ Text box</Button>
                <Button size="sm" variant="danger" onClick={remove} disabled={!selected}>Remove selected</Button>
              </div>
              {sel && selected ? (
                <div className="grid grid-cols-2 gap-2">
                  <Input id="sid" label="Id" value={sel.id} onChange={(e) => rename(selected.kind, sel.id, e.target.value)} hint={selected.kind === "opening" ? "the app fills openings in list order" : "e.g. nameLeft, days"} />
                  {selected.kind === "opening" && (
                    <Select id="ssh" label="Shape" value={(sel as OpeningMeta).shape} onChange={(e) => patch("opening", sel.id, { shape: e.target.value as SlotShape })}>
                      {["circle", "oval", "rounded", "square"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </Select>
                  )}
                  {(["cx", "cy", "w", "h"] as const).map((f) => (
                    <Input key={f} id={`n-${f}`} type="number" step="0.5" label={{ cx: "Centre X", cy: "Centre Y", w: "Width", h: "Height" }[f]} value={sel[f]}
                      onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) patch(selected.kind, sel.id, { [f]: v }); }} />
                  ))}
                </div>
              ) : <p className="text-xs text-ink-faint">Select a box to edit its numbers.</p>}

              <div>
                <p className="mb-1 text-xs font-medium text-ink-muted">Sample DP per opening (preview only, never saved)</p>
                <div className="flex flex-wrap gap-3">
                  {openings.map((o, i) => (
                    <label key={o.id} className="text-xs text-ink-muted">{o.id}: <input type="file" accept="image/*" className="w-44" onChange={(e) => onDp(i, e.target.files?.[0] ?? null)} /></label>
                  ))}
                </div>
              </div>

              {candidates.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium text-ink-muted">Detected transparent regions (only enclosed, round-ish ones are used automatically)</p>
                  <ul className="space-y-1">
                    {candidates.slice(0, 8).map((c, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 rounded border border-surface-border px-2 py-1 text-xs">
                        <span className="text-ink-muted">{c.likely ? "opening" : "ignored"} - {Math.round(c.w)}x{Math.round(c.h)} at ({Math.round(c.cx)}, {Math.round(c.cy)}) - {c.reason}</span>
                        <Button size="sm" variant="ghost" onClick={() => addOpening(c)}>Use</Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {problems.length > 0 && <ul className="list-disc pl-5 text-xs text-danger">{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
              <Button onClick={() => void save()} isLoading={busy} disabled={problems.length > 0}>Save frame metadata</Button>
              <p className="text-xs text-ink-faint">Saving needs the super_admin role. Existing apps keep a cached copy for up to ~10 minutes.</p>
            </Card>
          </div>
        )}

        {size && openings.length > 0 && (
          <Card className="p-4">
            <p className="mb-3 text-xs font-medium text-ink-muted">Preview at different display widths (same scaling the app uses)</p>
            <div className="flex flex-wrap items-end gap-6">
              {PREVIEW_WIDTHS.map((w) => (
                <div key={w}>
                  <FramePreview meta={{ canvasW: size.w, canvasH: size.h, openings, labels }} frameUrl={frameUrl} width={Math.min(w, size.w)} dpUrls={dpUrls.length ? dpUrls : [SAMPLE_DP]} />
                  <p className="mt-1 text-center text-[10px] text-ink-faint">{w}px</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
